import { useCallback, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { FilledButton } from 'components';

import useStyles from './ValidateButton.styles';
import { shareContext } from '../../ShareContext';
import { arrayEquals } from 'utils/array';
import { useMutation } from '@apollo/client';
import {
  SHARE_FOLDER_MUTATION,
  ShareFolderMutationOutput,
  UNSHARE_FOLDER_MUTATION,
  UnshareFolderMutationOutput,
} from 'graphql/folder';
import { useSelectedLinkContext } from 'contexts';
import { editContactsContext } from './EditContactsContext';
import { getHandleValidateFunction, updateCache } from './EditContactsHelpers';
import {useSnackbar} from 'hooks';

type ValidateButtonPureProps = {
  disabled: boolean;
  handleValidate: () => void;
};

const ValidateButtonPure = ({
  disabled,
  handleValidate,
}: ValidateButtonPureProps): JSX.Element => {
  const classes = useStyles();

  return (
    <FilledButton
      className={classes.button}
      color="violet"
      data-testid="validate-contacts-edit-button"
      disabled={disabled}
      onClick={handleValidate}
    >
      <FormattedMessage id="button.validate" />
    </FilledButton>
  );
};

const ValidateButton = (): JSX.Element => {
  const { selectedLink } = useSelectedLinkContext();
  const {
    selectedFileType,
    selectedFolder,
    setLoadingMessage,
    setSelectedFolder,
  } = useContext(shareContext);
  const { errorSnackbar, successSnackbar } = useSnackbar();
  const { contacts, setShowEditContactsModal } =
    useContext(editContactsContext);

  const initialContacts = (selectedFolder?.sharedWith || []).map((_) => _.id);
  const contactsHasChanged = !arrayEquals<string>(contacts, initialContacts);
  const addedContacts = contacts.filter((_) => !initialContacts.includes(_));
  const removedContacts = initialContacts.filter((_) => !contacts.includes(_));

  const [shareFolder] = useMutation<ShareFolderMutationOutput>(
    SHARE_FOLDER_MUTATION,
    {
      update(cache, { data }) {
        updateCache({
          cache,
          folder: data?.shareFolder.folder,
          selectedFileType,
          selectedLink,
        });
      },
    },
  );

  const [unshareFolder] = useMutation<UnshareFolderMutationOutput>(
    UNSHARE_FOLDER_MUTATION,
    {
      update(cache, { data }) {
        updateCache({
          cache,
          folder: data?.unshareFolder.folder,
          selectedFileType,
          selectedLink,
        });
      },
    },
  );

  const handleErrorResult = () => {
    errorSnackbar(<FormattedMessage id={`share.folder.share.error`} />);
    setLoadingMessage(undefined);
  };

  const handleValidateFunction = getHandleValidateFunction({
    addedContacts,
    removedContacts,
    selectedFolder,
    selectedLink,
    shareFolder,
    handleErrorResult,
    setLoadingMessage,
    setSelectedFolder,
    setShowEditContactsModal,
    successSnackbar,
    unshareFolder,
  });

  const handleValidate = useCallback(handleValidateFunction, [
    addedContacts,
    removedContacts,
    selectedFolder,
    selectedLink,
    shareFolder,
    handleErrorResult,
    handleValidateFunction,
    setLoadingMessage,
    setSelectedFolder,
    setShowEditContactsModal,
    successSnackbar,
    unshareFolder,
  ]);

  return (
    <ValidateButtonPure
      disabled={!contactsHasChanged}
      handleValidate={handleValidate}
    />
  );
};

export default ValidateButton;
